import React, { useMemo } from 'react';
import { Box, Button, IconButton, MenuItem, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AmountWithUnitField from '../common/AmountWithUnitField';
import SalovareSelectField from '../common/SalovareSelectField';
import SwitchField from '../common/SwitchField';
import PackageQuantityInput from './PackageQuantityInput';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';
import { getLocalizedValue } from '../../constants/Languages';
import { purchaseLineTotal, purchaseTotal } from '../../utils/stockPurchaseValidation';
import { toMoneyInputValue } from '../../utils/stockQuantityUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND } from '../../theme/brandTokens';

const productLabel = (product, language) => (
  typeof product?.name === 'string'
    ? product.name
    : getLocalizedValue(product?.name, language)
);

const emptyLine = () => ({
  key: `${Date.now()}-${Math.random()}`,
  productId: '',
  newProductName: '',
  stockUnit: 'Unit',
  contentQuantity: '1',
  isRetail: true,
  salePrice: '',
  packageQuantity: '1',
  unitCost: '',
  vatRate: '',
});

const StockPurchaseLinesField = ({ lines, products, currency, errors, onChange, canCreateProducts }) => {
  const { t, i18n } = useTranslation();
  const total = useMemo(() => purchaseTotal(lines), [lines]);

  const updateLine = (key, patch) => {
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  return (
    <Box id="stock-purchase-lines" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('stock.purchases.lines')}</Typography>
      {errors.lines ? (
        <Typography variant="caption" sx={{ color: 'error.main' }}>{t(errors.lines)}</Typography>
      ) : null}
      {lines.map((line, index) => (
        <Box key={line.key} sx={{ p: 2, border: `1px solid ${BRAND.line}`, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <SalovareSelectField
              id={`purchase-line-product-${index}`}
              label={t('stock.purchases.product')}
              value={line.productId}
              displayEmpty
              error={Boolean(errors[`line-${index}-product`])}
              helperText={errors[`line-${index}-product`] ? t(errors[`line-${index}-product`]) : undefined}
              onChange={(event) => {
                const productId = event.target.value;
                const product = products.find((item) => String(item.id) === String(productId));
                const unitCost = product?.lastUnitCost != null
                  ? toMoneyInputValue(product.lastUnitCost)
                  : line.unitCost;
                const vatRate = product
                  ? (product.vatExempt === false && product.vatRate != null ? String(product.vatRate) : '0')
                  : line.vatRate;
                updateLine(line.key, { productId, unitCost, ...(product ? { vatRate } : {}) });
              }}
              renderValue={(value) => {
                if (!value) {
                  return <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>{t('stock.purchases.newProduct')}</Typography>;
                }
                const selected = products.find((item) => String(item.id) === String(value));
                return productLabel(selected, i18n.language) || value;
              }}
            >
              <MenuItem value="">{t('stock.purchases.newProduct')}</MenuItem>
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>{productLabel(product, i18n.language)}</MenuItem>
              ))}
            </SalovareSelectField>
            <IconButton aria-label={t('stock.purchases.removeLine')} onClick={() => onChange(lines.filter((item) => item.key !== line.key))} sx={{ color: 'error.main' }}>
              <DeleteIcon />
            </IconButton>
          </Box>

          {!line.productId && canCreateProducts ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.purchases.newProductName')}</Typography>
                <Box
                  component="input"
                  id={`purchase-line-new-name-${index}`}
                  aria-label={t('stock.purchases.newProductName')}
                  value={line.newProductName}
                  onChange={(event) => updateLine(line.key, { newProductName: event.target.value })}
                  sx={nativeInputSx(Boolean(errors[`line-${index}-product`]))}
                />
              </Box>
              <AmountWithUnitField
                id={`purchase-line-content-${index}`}
                label={t('productsManagement.fields.contentQuantity')}
                required
                quantity={line.contentQuantity}
                stockUnit={line.stockUnit}
                error={errors[`line-${index}-content`] ? t(errors[`line-${index}-content`]) : undefined}
                onQuantityChange={(contentQuantity) => updateLine(line.key, { contentQuantity })}
                onUnitChange={(stockUnit) => updateLine(line.key, stockUnit === 'Unit'
                  ? { stockUnit, contentQuantity: '1' }
                  : { stockUnit })}
              />
              <SwitchField
                id={`purchase-line-retail-${index}`}
                checked={line.isRetail !== false}
                onChange={(event) => updateLine(line.key, { isRetail: event.target.checked })}
                label={t('productsManagement.fields.soldToCustomers')}
              />
              {line.isRetail !== false ? (
                <PackageQuantityInput
                  id={`purchase-line-price-${index}`}
                  label={t('productsManagement.fields.price')}
                  required
                  step="0.01"
                  value={line.salePrice}
                  error={errors[`line-${index}-price`] ? t(errors[`line-${index}-price`]) : undefined}
                  onChange={(salePrice) => updateLine(line.key, { salePrice })}
                />
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
            <PackageQuantityInput
              id={`purchase-line-qty-${index}`}
              label={t('stock.purchases.packageQuantity')}
              required
              min={0}
              value={line.packageQuantity}
              error={errors[`line-${index}-quantity`] ? t(errors[`line-${index}-quantity`]) : undefined}
              onChange={(packageQuantity) => updateLine(line.key, { packageQuantity })}
            />
            <PackageQuantityInput
              id={`purchase-line-cost-${index}`}
              label={t('stock.purchases.unitCost')}
              required
              step="0.01"
              maxDecimals={2}
              value={line.unitCost}
              error={errors[`line-${index}-cost`] ? t(errors[`line-${index}-cost`]) : undefined}
              onChange={(unitCost) => updateLine(line.key, { unitCost })}
            />
            <PackageQuantityInput
              id={`purchase-line-vat-${index}`}
              label={t('stock.purchases.vatRate')}
              required
              min={0}
              step="0.01"
              value={line.vatRate}
              error={errors[`line-${index}-vat`] ? t(errors[`line-${index}-vat`]) : undefined}
              helperText={t('stock.purchases.vatRateHelp')}
              onChange={(vatRate) => updateLine(line.key, { vatRate })}
            />
          </Box>
          <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600 }}>
            {formatCurrency(purchaseLineTotal(line), currency)}
          </Typography>
        </Box>
      ))}
      <Button
        id="purchase-add-line"
        type="button"
        startIcon={<AddIcon />}
        onClick={() => onChange([...lines, emptyLine()])}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: BRAND.accentText }}
      >
        {t('stock.purchases.addLine')}
      </Button>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'right' }}>
        {t('stock.purchases.total')}: {formatCurrency(total, currency)}
      </Typography>
    </Box>
  );
};

export const createPurchaseLine = emptyLine;

export const createPurchaseLineFromProduct = (product) => {
  const line = emptyLine();
  if (!product) {
    return line;
  }

  return {
    ...line,
    productId: product.id,
    unitCost: toMoneyInputValue(product.lastUnitCost),
    vatRate: product.vatExempt === false && product.vatRate != null ? String(product.vatRate) : '0',
  };
};

export default StockPurchaseLinesField;
